import React, { useEffect, useRef, useState } from 'react';
import styles from './MfImportFile.module.css';
import { UploadCloudIcon } from 'features/compositions/assets/icons/icons';
import useTheme from 'config/useTheme';
import { useMfImport } from 'features/compositions/context/MfImportContext';
import { useParseMf } from 'features/compositions/services/useMfImportService';
import ActionLoader from 'components/shared/status/ActionLoader';
import ActionFailed from 'components/shared/status/ActionFailed';

const MfImportFile = () => {
    const { dark } = useTheme();
    const { setFile, setStep } = useMfImport();
    const fileInputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);

    const allowedExtensions = ['csv', 'json', 'xml'];

    const {
        response,
        loading,
        error,
        parseMf,
        resetServerStatus,
    } = useParseMf();

    useEffect(() => {
        if (response?.status) {
            setStep(2);
        }
    }, [response?.status]);

    useEffect(() => {
        if (response) {
            const timer = setTimeout(() => {
                resetServerStatus();
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [response]);
    

    const isValidFile = (file) => {
        const extension = file.name.split('.').pop().toLowerCase();
        return allowedExtensions.includes(extension);
    };

    const handleParseMf = (file) => {
        const type = file.name.split('.').pop().toLowerCase();
        resetServerStatus();
        parseMf({ file, type });
    };

    const processFile = (file) => {
        if (!isValidFile(file)) {
            // alert('Only .csv, .json, or .xml files are allowed.');
            alert('Only .csv, .json, or .xml files are allowed.');
            return false;
        }

        setFile(file);
        handleParseMf(file);
        return true;
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            processFile(files[0]);
            e.target.value = null;
        }
    };
    

    const handleDrop = async (e) => {
        e.preventDefault();
        setDragOver(false);

        const items = e.dataTransfer.items;
        if (items && items.length > 0) {
            const files = await processDroppedItems(items);
            if (files.length > 0) {
                processFile(files[0]);
            }
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const processDroppedItems = async (items) => {
        const files = [];

        const traverseEntry = async (entry) => {
            if (entry.isFile) {
                const file = await new Promise((resolve, reject) => {
                    entry.file(resolve, reject);
                });
                if (isValidFile(file)) {
                    files.push(file);
                }
            } else if (entry.isDirectory) {
                const reader = entry.createReader();
                const entries = await new Promise((resolve, reject) => {
                    reader.readEntries(resolve, reject);
                });
                for (const child of entries) {
                    await traverseEntry(child);
                }
            }
        };

        for (const item of items) {
            const entry = item.webkitGetAsEntry();
            if (entry) {
                await traverseEntry(entry);
            }
        }

        return files;
    };

    return (
        <div
            className={`${styles.mf_import_file} ${dragOver ? styles.active : ''} ${dark ? styles.dark : ''}`}
            onClick={handleClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
        >
            {loading && <ActionLoader text="Reading and parsing your file..." />}
            {error && <ActionFailed text={error} />}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                // accept=".csv,.json,.xml"
                accept=".csv,.json,.xml"
            />
            <UploadCloudIcon />
            <button type="button">
                Import or drag your file
            </button>
        </div>
    );
};

export default MfImportFile;